import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// –––––––––––––––––––––––––––––––––––––––––––––––– //
// ————————————————–– Helper Functions ––—————————– //
// –––––––––––––––––––––––––––––––––––––––––––––––– //

// Reusable function for animating illustrations with parallax
const animateIllustrations = (illustrations, progress, scrollDistance) => {
	illustrations.forEach((illustration) => {
		const speed = illustration.dataset.speed || 0.6;
		gsap.set(illustration, {
			y: -progress * scrollDistance * speed,
		});
	});
};

const initHomepage = () => {
	// –––––––––––––––––––––––––––––––––––––––––––––––– //
	// ————————————————–– Home Hero –—————————————————— //
	// –––––––––––––––––––––––––––––––––––––––––––––––– //
	const homeHero = document.getElementById("home-hero-section");

	if (homeHero) {
		const homeHeroContent = homeHero.querySelector(".home-hero-content");
		const homeHeroVideo = homeHero.querySelector(".home-hero-video");
		const homeHeroIllustration = homeHero.querySelectorAll(".home-hero-illustration");
		let viewportHeight = window.innerHeight;
		let scrollDistance = Math.max(viewportHeight);

		ScrollTrigger.create({
			trigger: homeHero,
			start: `top top`,
			end: `+=${scrollDistance}`,
			scrub: true,
			pin: true,
			pinSpacing: false,
			onUpdate: (self) => {
				const progress = self.progress;
				const clipStart = 34;
				const clipSpeed = 2;
				const clipProgress = clipStart - clipStart * Math.min(progress * clipSpeed, 1);

				gsap.set(homeHeroContent, {
					y: -progress * scrollDistance * 0.4,
				});

				animateIllustrations(homeHeroIllustration, progress, scrollDistance);

				// Video reveal
				gsap.set(homeHeroVideo, {
					y: -progress * scrollDistance,
					clipPath: `polygon(${clipProgress}% 0, ${100 - clipProgress}% 0, ${
						100 - clipProgress
					}% 100%, ${clipProgress}% 100%)`,
				});
			},
			invalidateOnRefresh: true,
		});
	}

	// –––––––––––––––––––––––––––––––––––––––––––––––– //
	// ————————————————–– Home Quote –————————————————— //
	// –––––––––––––––––––––––––––––––––––––––––––––––– //
	const homeQuote = document.getElementById("home-quote-section");

	if (homeQuote) {
		const homeQuoteIllustration = homeQuote.querySelectorAll(".home-quote-illustration");
		let containerHeight = homeQuote.offsetHeight;
		let viewportHeight = window.innerHeight;
		let scrollDistance = Math.max(containerHeight, viewportHeight);

		ScrollTrigger.create({
			trigger: homeQuote,
			start: `top top`,
			end: `+=${scrollDistance}`,
			scrub: true,
			onUpdate: (self) => {
				animateIllustrations(homeQuoteIllustration, self.progress, scrollDistance);
			},
			invalidateOnRefresh: true,
		});
	}

	// –––––––––––––––––––––––––––––––––––––––––––––––– //
	// ———– Home Amenities Clip Path Scroll Reveal –——— //
	// –––––––––––––––––––––––––––––––––––––––––––––––– //
	const homeAmenities = document.querySelector("[data-target='home-amenities-slides']");
	const homeAmenitiesFixedSlides = homeAmenities?.querySelectorAll("[data-fixed-slide]");

	if (homeAmenities) {
		const slides = gsap.utils.toArray(homeAmenitiesFixedSlides);
		const numSlides = slides.length;

		// Set initial z-index and clip-path for stacking
		slides.forEach((slide, index) => {
			gsap.set(slide, {
				zIndex: index + 1,
			});

			// Set initial clip-path for all slides except the first
			if (index > 0) {
				gsap.set(slide, {
					clipPath: "inset(100% 0% 0% 0%)",
				});
			}
		});

		// Pin the main container and handle all slide animations
		ScrollTrigger.create({
			trigger: homeAmenities,
			start: "top top",
			end: () => `+=${(numSlides - 1) * window.innerHeight}`,
			pin: true,
			pinSpacing: true,
			scrub: true,
			onUpdate: (self) => {
				const progress = self.progress;

				slides.forEach((slide, index) => {
					const image = slide.querySelector(".slide-image");
					const slideStart = index > 0 ? (index - 1) / (numSlides - 1) : 0;
					const parallaxOffset = 50;
					const parallaxRange = 100;
					const offset = index * parallaxOffset;
					gsap.set(image, {
						yPercent: offset - progress * parallaxRange,
						willChange: "transform",
					});

					// Clip-path reveal
					if (index > 0) {
						const slideEnd = index / (numSlides - 1);
						const slideProgress = Math.max(
							0,
							Math.min(1, (progress - slideStart) / (slideEnd - slideStart))
						);
						const insetTop = 100 - slideProgress * 100;

						gsap.set(slide, {
							clipPath: `inset(${insetTop}% 0% 0% 0%)`,
						});
					}
				});
			},
			invalidateOnRefresh: true,
		});
	}
	// –––––––––––––––––––––––––––––––––––––––––––––––– //
	// ——————————— Animated SVG Line Paths ———————————— //
	// –––––––––––––––––––––––––––––––––––––––––––––––– //
	const animateSvgLine = (container, scrollTriggerOptions = {}) => {
		const maskPath = container?.querySelector(".mask-path");

		if (maskPath) {
			const pathLength = maskPath.getTotalLength();

			// Set initial state - mask path is hidden
			// Force GPU acceleration for Safari performance
			gsap.set(maskPath, {
				strokeDasharray: pathLength,
				strokeDashoffset: pathLength,
				force3D: true,
				willChange: "stroke-dashoffset",
			});

			// Default ScrollTrigger options
			const defaultOptions = {
				trigger: container,
				start: "top bottom",
				end: "bottom top",
				scrub: 0.5,
			};

			// Animate the mask path drawing with scroll
			gsap.to(maskPath, {
				strokeDashoffset: 0,
				ease: "none",
				force3D: true,
				scrollTrigger: {
					...defaultOptions,
					...scrollTriggerOptions,
					onLeave: () => {
						gsap.set(maskPath, { willChange: "auto" });
					},
					onEnterBack: () => {
						gsap.set(maskPath, { willChange: "stroke-dashoffset" });
					},
				},
			});
		}
	};

	// Animate all SVG lines
	const svgLines = [
		{
			selector: ".from-hero-line",
			options: { start: "top center", end: "bottom 60%", scrub: true },
		},
		{
			selector: ".to-amenities-line",
			options: { start: "top center", end: "bottom 60%", scrub: true },
		},
		{
			selector: ".to-town-center-line",
			options: { start: "top 90%", end: "bottom 50%", scrub: true },
		},
		{
			selector: ".from-town-center-line",
			options: { start: "top 90%", end: "bottom 50%", scrub: true },
		},
	];

	svgLines.forEach(({ selector, options }) => {
		const line = document.querySelector(selector);
		if (line) {
			animateSvgLine(line, options);
		}
	});
};

export default initHomepage;
